// v19.7 — DAW Keyboard Shortcut cheatsheet overlay.
//
// Displayed as a small floating panel (NOT a full-screen modal) so the
// producer can leave it open while continuing to work — clicks pass
// through to the DAW behind it. Dismissed by:
//   • clicking the × in the header,
//   • pressing Escape, or
//   • clicking the "Shortcut List" TopBar button again (parent toggles).
//
// The listed shortcuts mirror the actual `keydown` handlers currently
// wired in the codebase (grep-verified — see agent notes in commit
// message). Update this list whenever a new key binding is added.
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { X, Keyboard } from "@phosphor-icons/react";

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform);
const MOD = isMac ? "⌘" : "Ctrl";

const SECTIONS = [
  {
    title: "Transport",
    rows: [
      { keys: ["Space"], desc: "Play / Pause" },
    ],
  },
  {
    title: "Project",
    rows: [
      { keys: [`${MOD}`, "Z"], desc: "Undo last action" },
    ],
  },
  {
    title: "Arranger — Regions & Audio Clips",
    rows: [
      { keys: ["Delete"], desc: "Delete selected clip(s)" },
      { keys: ["Backspace"], desc: "Delete selected clip(s) (alt)" },
      { keys: [`${MOD}`, "D"], desc: "Smart duplicate — clones the selection one span to the right" },
      { keys: [`${MOD}`, "C"], desc: "Copy selection to clipboard" },
      { keys: [`${MOD}`, "V"], desc: "Paste clipboard at playhead" },
      { keys: [`${MOD}`, "J"], desc: "Join / merge selected regions (2+, same track)" },
      { keys: ["R"], desc: "Reverse selected region or audio clip" },
      { keys: ["↑"], desc: "Transpose selection +1 semitone" },
      { keys: ["↓"], desc: "Transpose selection −1 semitone" },
      { keys: ["Shift", "↑"], desc: "Shift selection UP one octave (+12 semitones)" },
      { keys: ["Shift", "↓"], desc: "Shift selection DOWN one octave (−12 semitones)" },
      { keys: ["C"], desc: "Toggle Scissor tool (cut regions)" },
      { keys: ["V"], desc: "Switch back to Cursor tool" },
    ],
  },
  {
    title: "Piano Roll — Notes",
    rows: [
      { keys: ["Delete"], desc: "Delete selected notes" },
      { keys: ["Backspace"], desc: "Delete selected notes (alt)" },
      { keys: [`${MOD}`, "D"], desc: "Duplicate selected notes just past the selection" },
    ],
  },
  {
    title: "Audio Editing",
    rows: [
      // v19.24 — Time-stretch discoverability. Producers won't guess
      // Alt-drag unless we surface it in the shortcut list.
      { keys: ["Alt", "Drag Clip Edge"], desc: "Stretch / compress audio clip speed (drag right = slower, drag left = faster). Double-click handle to reset to 1×." },
      { keys: ["Drag Clip Edge"], desc: "Trim head or tail of an audio clip without changing speed" },
      { keys: ["Drag Clip Body"], desc: "Move an audio clip along the timeline (or across tracks)" },
      // v19.26 — Audio clip reverse.
      { keys: ["R"], desc: "Reverse selected audio clip (toggle). Waveform flips to show reversed state." },
      // v19.28 — Pitch reset via badge double-click. The actual
      // pitch keys live in the Arranger section since they work on
      // both MIDI regions and audio clips.
      { keys: ["Dbl-click pitch badge"], desc: "Reset selected audio clip's pitch offset back to 0" },
    ],
  },
];

function KeyCap({ label }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[24px] h-[22px] px-1.5 rounded border border-[#333] bg-[#0f0f0f] text-[10px] font-mono text-white/90 shadow-[inset_0_-1px_0_#000]">
      {label}
    </kbd>
  );
}

export default function ShortcutListOverlay({ open, onClose }) {
  const [pos, setPos] = useState({ x: 24, y: 72 });
  const dragState = useRef(null);

  // Close on Escape — capture-phase so a focused input doesn't swallow.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [open, onClose]);

  if (!open) return null;

  // Simple drag-by-header so the producer can shove it out of the way
  // without dismissing. Kept intentionally lightweight (no library).
  const onHeaderMouseDown = (e) => {
    if (e.button !== 0) return;
    dragState.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
    e.preventDefault();
    const move = (ev) => {
      if (!dragState.current) return;
      const dx = ev.clientX - dragState.current.startX;
      const dy = ev.clientY - dragState.current.startY;
      setPos({
        x: Math.max(8, Math.min(window.innerWidth - 340, dragState.current.origX + dx)),
        y: Math.max(8, Math.min(window.innerHeight - 60, dragState.current.origY + dy)),
      });
    };
    const up = () => {
      dragState.current = null;
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  return ReactDOM.createPortal(
    <div
      data-testid="shortcut-overlay"
      className="fixed z-[900] w-[340px] rounded-lg border border-[#2a2a2a] bg-[#0a0a0a]/95 backdrop-blur-md shadow-[0_20px_60px_-15px_rgba(0,0,0,0.9)] text-white/85 font-sans"
      style={{ left: pos.x, top: pos.y }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className="flex items-center justify-between px-3 py-2 border-b border-[#1f1f1f] cursor-grab active:cursor-grabbing select-none"
        onMouseDown={onHeaderMouseDown}
        data-testid="shortcut-overlay-header"
      >
        <div className="flex items-center gap-2">
          <Keyboard size={14} weight="bold" className="text-white/60" />
          <span className="text-[11px] font-bold tracking-wider uppercase">Shortcuts</span>
        </div>
        <button
          className="w-6 h-6 rounded hover:bg-[#1a1a1a] flex items-center justify-center text-white/60 hover:text-white/90"
          onClick={onClose}
          data-testid="shortcut-overlay-close"
          title="Close (Esc)"
        >
          <X size={12} weight="bold" />
        </button>
      </div>

      <div className="max-h-[62vh] overflow-y-auto px-3 py-2 space-y-3">
        {SECTIONS.map((sec) => (
          <div key={sec.title} className="space-y-1.5">
            <div className="text-[9px] font-mono tracking-[0.15em] uppercase text-white/40 pt-1">
              {sec.title}
            </div>
            <div className="space-y-1">
              {sec.rows.map((r, i) => (
                <div key={i} className="flex items-center justify-between gap-3 py-0.5">
                  <div className="flex items-center gap-1 shrink-0">
                    {r.keys.map((k, ki) => (
                      <React.Fragment key={ki}>
                        {ki > 0 && <span className="text-white/30 text-[10px]">+</span>}
                        <KeyCap label={k} />
                      </React.Fragment>
                    ))}
                  </div>
                  <div className="text-[11px] text-white/70 text-right leading-tight">{r.desc}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="pt-2 pb-1 text-[9px] text-white/30 text-center font-mono">
          drag header to move · esc to close
        </div>
      </div>
    </div>,
    document.body
  );
}
